package top.wcpe.allincore.ui.runtime.ir.call

import top.wcpe.allincore.dsl.ir.expr.ExprNode
import top.wcpe.allincore.ui.runtime.animation.AnimationHandle
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.event.UiEventHandler
import top.wcpe.allincore.ui.runtime.host.AnimationHost
import top.wcpe.allincore.ui.runtime.host.ToastManager
import top.wcpe.allincore.ui.runtime.host.UiTask
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.internal.math.mathRoundCompat
import top.wcpe.allincore.ui.runtime.internal.text.formatStringCompat
import top.wcpe.allincore.ui.runtime.ir.DslExecutor
import top.wcpe.allincore.ui.runtime.ir.DslRuntimeError
import top.wcpe.allincore.ui.runtime.ir.DslValueUtils
import top.wcpe.allincore.ui.runtime.ir.eval.EvalContext
import top.wcpe.allincore.ui.runtime.ir.eval.ExprEvaluator
import top.wcpe.allincore.ui.runtime.ir.eval.ValueCoercion
import top.wcpe.allincore.ui.runtime.ir.subscribe.Subscription
import top.wcpe.allincore.ui.runtime.ir.subscribe.SubscriptionValue
import top.wcpe.allincore.ui.runtime.theme.ColorFunctions
import top.wcpe.allincore.ui.runtime.theme.ControlTheme
import top.wcpe.allincore.ui.runtime.theme.ThemeAccess
import top.wcpe.allincore.ui.runtime.theme.ThemeLookup
import top.wcpe.allincore.ui.runtime.theme.ToastStyle
import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.atan2
import kotlin.math.ceil
import kotlin.math.cos
import kotlin.math.floor
import kotlin.math.max
import kotlin.math.min
import kotlin.math.roundToLong
import kotlin.math.sin
import kotlin.math.sqrt
import kotlin.math.tan

/**
 * 内建调用求值（拆分自 [CallDispatcher]）。
 *
 * 集中处理 DSL 中所有"内建函数"形态：
 * - 输出：`print(v)` / `log(v)` → [top.wcpe.allincore.ui.runtime.ir.DslLogger]
 * - 数学：一元 / 二元 / 三元数学函数、几何 hit-test
 * - 颜色：`rgba` / `hsva` / `lighten` / `darken` / `alpha` / `mix`
 * - 文本：`format`
 * - 通知：`toast`
 * - 动画：`animate`
 * - 类型转换：`Number` / `Boolean`
 * - 协议级订阅：`net.subscribe`（[evalNetSubscribe]）+ handler 包装 [wrapSubscribeCallable]
 *
 * 依赖 DAG：处于 [CallArgs] + [CallableInvoker] 之上——builtin 回调 / animate onComplete /
 * net.subscribe handler 经 [CallableInvoker.invokeCallable] 调用，数值强转走 [CallArgs.toDouble]。
 */
@Suppress("TooManyFunctions", "LargeClass", "MagicNumber")
internal object BuiltinCallEvaluator {

    private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.ir.call.BuiltinCallEvaluator")

    // SPLICE_BUILTIN

    /**
     * 内建函数调用分派（RFC §3.3）：
     * - 输出：`print(v)` / `log(v)` → [top.wcpe.allincore.ui.runtime.ir.DslLogger]
     * - 异步：`delay(ms)`（仅 suspend 上下文，否则抛错）
     * - 一元数学：`sin/cos/tan/abs/sqrt/floor/ceil/round/fract`
     * - 多元数学：`min/max/mod` (二元) / `clamp/lerp/smoothstep` (三元)
     * - 颜色构造：`rgba(r,g,b,a)` / `hsva(h,s,v,a)`，输出为 ARGB int 装箱为 double
     */
    @Suppress("CyclomaticComplexMethod", "LongMethod", "ReturnCount", "ThrowsCount", "CognitiveComplexMethod")
    fun evalBuiltinCall(call: ExprNode.BuiltinCall, ctx: EvalContext): Any? {
        val name = call.name
        val args = call.args
        when (name) {
            "print", "log" -> return evalPrintLog(name, args, ctx)
            "delay" ->
                // delay 由状态机路径处理；此处到达说明 IR 形态异常（delay 必须在 suspend 上下文中使用）
                throw DslRuntimeError("delay() 应由状态机执行；若到达此处说明 IR 形态异常（delay 必须在 suspend 上下文中使用）")
            // ===== 一元数学 =====
            "sin" -> return sin(num1(name, args, ctx))
            "cos" -> return cos(num1(name, args, ctx))
            "tan" -> return tan(num1(name, args, ctx))
            "abs" -> return abs(num1(name, args, ctx))
            "sqrt" -> return sqrt(num1(name, args, ctx))
            "floor" -> return floor(num1(name, args, ctx))
            "ceil" -> return ceil(num1(name, args, ctx))
            "round" ->
                // 与多数运行时一致：返回 double，便于继续参与算术
                return mathRoundCompat(num1(name, args, ctx)).toDouble()
            "fract" -> {
                val x = num1(name, args, ctx)
                return x - floor(x)
            }
            // ===== 二元数学 =====
            "min" -> {
                val ab = num2(name, args, ctx)
                return min(ab[0], ab[1])
            }
            "max" -> {
                val ab = num2(name, args, ctx)
                return max(ab[0], ab[1])
            }
            "mod" -> {
                // 浮点取模，语义对齐 GLSL mod：x - y * floor(x / y)
                val ab = num2(name, args, ctx)
                val a = ab[0]
                val b = ab[1]
                if (b == 0.0) {
                    throw DslRuntimeError("mod() 除数不能为 0")
                }
                return a - b * floor(a / b)
            }
            // ===== 三元数学 =====
            "clamp" -> {
                val xyz = numN(name, args, ctx, 3)
                return max(xyz[1], min(xyz[2], xyz[0]))
            }
            "lerp" -> {
                val xyz = numN(name, args, ctx, 3)
                return xyz[0] + (xyz[1] - xyz[0]) * xyz[2]
            }
            "smoothstep" -> {
                val xyz = numN(name, args, ctx, 3) // edge0, edge1, x
                val edge0 = xyz[0]
                val edge1 = xyz[1]
                val x = xyz[2]
                if (edge0 == edge1) {
                    // 退化区间：与 GLSL 一致返回 0（避免除 0）
                    return 0.0
                }
                val t = max(0.0, min(1.0, (x - edge0) / (edge1 - edge0)))
                return t * t * (3.0 - 2.0 * t)
            }
            // ===== v4 C.3：几何 hit-test 内建函数（纯算术、无副作用） =====
            "hitRect" -> {
                // hitRect(mx, my, x, y, w, h)：AABB 半开区间命中判定
                val a = numN(name, args, ctx, 6)
                val mx = a[0]
                val my = a[1]
                val x = a[2]
                val y = a[3]
                val w = a[4]
                val h = a[5]
                return mx >= x && mx < x + w && my >= y && my < y + h
            }
            "hitCircle" -> {
                // hitCircle(mx, my, cx, cy, r)：圆形命中判定 dx² + dy² ≤ r²
                val a = numN(name, args, ctx, 5)
                val dx = a[0] - a[2]
                val dy = a[1] - a[3]
                val r = a[4]
                return dx * dx + dy * dy <= r * r
            }
            "hitArc" -> {
                // hitArc(mx, my, cx, cy, rInner, rOuter, a0, a1)：环形扇区命中判定
                // 角度归一化到 [0, 2π)，支持跨 0 角扇区（如 a0=350°, a1=10°）
                val a = numN(name, args, ctx, 8)
                val mx = a[0]
                val my = a[1]
                val cx = a[2]
                val cy = a[3]
                val rInner = a[4]
                val rOuter = a[5]
                val a0 = a[6]
                val a1 = a[7]
                val dx = mx - cx
                val dy = my - cy
                val dist2 = dx * dx + dy * dy
                if (dist2 < rInner * rInner || dist2 > rOuter * rOuter) {
                    return false
                }
                // 角度归一化到 [0, 2π)
                val twoPi = PI * 2.0
                var theta = atan2(dy, dx)
                if (theta < 0) theta += twoPi
                var na0 = a0 % twoPi
                if (na0 < 0) na0 += twoPi
                var na1 = a1 % twoPi
                if (na1 < 0) na1 += twoPi
                return if (na0 <= na1) {
                    theta >= na0 && theta < na1
                } else {
                    // 跨 0 角扇区
                    theta >= na0 || theta < na1
                }
            }
            // ===== 颜色构造 =====
            "rgba" -> {
                val rgba = numN(name, args, ctx, 4)
                val r = clampByteChannel(rgba[0])
                val g = clampByteChannel(rgba[1])
                val b = clampByteChannel(rgba[2])
                val a = clampByteChannel(rgba[3])
                val argb = (a shl 24) or (r shl 16) or (g shl 8) or b
                // double 可精确表示任意 32 位整数；与 DrawInterpreter#toColor 兼容
                return (argb.toLong() and 0xFFFFFFFFL).toDouble()
            }
            "hsva" -> {
                val hsva = numN(name, args, ctx, 4)
                val argb = hsvaToArgb(hsva[0], hsva[1], hsva[2], hsva[3])
                return (argb.toLong() and 0xFFFFFFFFL).toDouble()
            }
            // ===== 颜色函数（运行时，与编译期 ColorMath 对齐；返回 #AARRGGBB 字符串）=====
            "lighten" -> {
                if (args.size != 2) {
                    throw DslRuntimeError("lighten() 需要 2 个参数（颜色, 量 0..1），实际 ${args.size}")
                }
                val c = ValueCoercion.toIntColor(ExprEvaluator.eval(args[0], ctx))
                return ColorFunctions.lighten(c, CallArgs.toDouble(name, ExprEvaluator.eval(args[1], ctx)))
            }
            "darken" -> {
                if (args.size != 2) {
                    throw DslRuntimeError("darken() 需要 2 个参数（颜色, 量 0..1），实际 ${args.size}")
                }
                val c = ValueCoercion.toIntColor(ExprEvaluator.eval(args[0], ctx))
                return ColorFunctions.darken(c, CallArgs.toDouble(name, ExprEvaluator.eval(args[1], ctx)))
            }
            "alpha" -> {
                if (args.size != 2) {
                    throw DslRuntimeError("alpha() 需要 2 个参数（颜色, alpha 0..1），实际 ${args.size}")
                }
                val c = ValueCoercion.toIntColor(ExprEvaluator.eval(args[0], ctx))
                return ColorFunctions.alpha(c, CallArgs.toDouble(name, ExprEvaluator.eval(args[1], ctx)))
            }
            "mix" -> {
                if (args.size != 3) {
                    throw DslRuntimeError("mix() 需要 3 个参数（颜色1, 颜色2, 比例 0..1），实际 ${args.size}")
                }
                val c1 = ValueCoercion.toIntColor(ExprEvaluator.eval(args[0], ctx))
                val c2 = ValueCoercion.toIntColor(ExprEvaluator.eval(args[1], ctx))
                return ColorFunctions.mix(c1, c2, CallArgs.toDouble(name, ExprEvaluator.eval(args[2], ctx)))
            }
            // M6-f §8.4：字符串格式化（仅支持 %d / %f / %s / %.Nf 子集）
            "format" -> return evalFormat(args, ctx)
            // M6-h Round 2：toast 通知（任意上下文可调用，含同步 fun / lambda / 事件）
            "toast" -> return evalToast(call, ctx)
            // M6-i：animate(name, target?, onComplete?) 程序化触发动画
            "animate" -> return evalAnimate(call, ctx)
            "Number" -> return evalNumberConversion(args, ctx)
            "Boolean" -> return evalBooleanConversion(args, ctx)
            else -> throw DslRuntimeError("未知内建调用: $name")
        }
    }

    /** PAPI pipe 使用的 Number(value) 转换：失败返回 null，方便 DSL 侧用 ?: 兜底。 */
    @Suppress("ReturnCount")
    private fun evalNumberConversion(args: List<ExprNode>, ctx: EvalContext): Any? {
        if (args.size != 1) {
            throw DslRuntimeError("Number() 需要 1 个参数，实际 ${args.size}")
        }
        val value = ExprEvaluator.eval(args[0], ctx)
        if (value == null) return null
        if (value is Number) return value.toDouble()
        if (value is Boolean) return if (value) 1.0 else 0.0
        if (value is String) {
            val text = value.trim()
            if (text.isEmpty()) return null
            return try {
                text.toDouble()
            } catch (ignored: NumberFormatException) {
                null
            }
        }
        return null
    }

    /** PAPI pipe 使用的 Boolean(value) 转换：失败返回 null，方便 DSL 侧用 ?: 兜底。 */
    @Suppress("ReturnCount", "ComplexCondition")
    private fun evalBooleanConversion(args: List<ExprNode>, ctx: EvalContext): Any? {
        if (args.size != 1) {
            throw DslRuntimeError("Boolean() 需要 1 个参数，实际 ${args.size}")
        }
        val value = ExprEvaluator.eval(args[0], ctx)
        if (value == null) return null
        if (value is Boolean) return value
        if (value is Number) return value.toDouble() != 0.0
        if (value is String) {
            val text = value.trim().lowercase()
            if (text == "true" || text == "1" || text == "yes") return true
            if (text == "false" || text == "0" || text == "no" || text.isEmpty()) return false
        }
        return null
    }

    /**
     * Stage B：求值 `net.subscribe("ns.topic", handler)` IR 节点。
     *
     * 由 [ExprEvaluator.eval] 在识别到 [ExprNode.NetSubscribe] 时直接调用——
     * 与 net.request (StmtNode.Request) 同属"协议级内置调用"分类，不走通用 BuiltinCall 路径，
     * namespace 与 topic 由 IR 直接读字段，不再以 String 实参形态出现在 args 列表中。
     *
     * handler 形态由 IR 保证为可调用对象（LambdaValue / BoundFunRefValue / FunRefValue /
     * LocalFunValue），编译期 SubscribeChecker 已校验形态；此处仅做 runtime 类型兜底。
     * 返回 SubscriptionValue 供 DSL 持有，调 `s.cancel()` 取消订阅。
     */
    @Suppress("ComplexCondition")
    fun evalNetSubscribe(node: ExprNode.NetSubscribe, ctx: EvalContext): Any {
        val handlerVal = ExprEvaluator.eval(node.handler, ctx)
        if (handlerVal !is LambdaValue &&
            handlerVal !is BoundFunRefValue &&
            handlerVal !is FunRefValue &&
            handlerVal !is LocalFunValue
        ) {
            throw DslRuntimeError(
                "net.subscribe handler 必须为可调用对象，实际类型: ${DslValueUtils.classOf(handlerVal)}",
            )
        }
        val namespace = node.namespace
        val topic = node.topic
        val consumer: UiEventHandler<Map<String, Any?>> =
            wrapSubscribeCallable(handlerVal, ctx, namespace, topic, "net.subscribe-handler")
        val router = ctx.subscribeRouter
        val sub: Subscription = router.subscribe(namespace, topic, consumer)
        return SubscriptionValue(sub)
    }

    /**
     * B7/B8：把"DSL 可调用对象"包装为 `UiEventHandler<Map<String, Object>>`，统一处理调度与异常隔离。
     *
     * 所有路径共用：
     * - `IrInterpreter.buildSubscribeHandler`（funRef 形态）— 通过 `ExprEvaluator.eval`
     *   把 IR 中的 FunRef 节点求值为 FunRefValue 后传入
     * - `CallDispatcher.evalNetSubscribe`（动态订阅）— DSL 内 `net.subscribe(...)`
     *   第 3 参直接是已求值的可调用对象
     *
     * 调度策略：当 [EvalContext.executor] 不为空时把 handler 调用提交到 DslExecutor
     * 串行化，否则同步在分发线程执行（保留同步测试模式兼容）。
     *
     * 异常策略：handler 内部抛出的任何 Throwable 都被吞掉并以 WARN 记录，不影响后续 dispatch
     * 派发到其它 handler——与 `DefaultSubscribeRouter.dispatch` 内的隔离形成两级保护。
     */
    fun wrapSubscribeCallable(
        callable: Any?,
        ctx: EvalContext,
        namespace: String,
        topic: String,
        hint: String,
    ): UiEventHandler<Map<String, Any?>> {
        val executor: DslExecutor? = ctx.executor
        return UiEventHandler { payload ->
            val task = UiTask {
                try {
                    val callArgs: List<Any?> = listOf(payload)
                    CallableInvoker.invokeCallable(callable, callArgs, ctx, hint)
                } catch (t: Throwable) {
                    LOGGER.warn("订阅 handler 运行期执行异常 hint=$hint namespace=$namespace topic=$topic", t)
                }
            }
            if (executor != null) {
                executor.submit(task)
            } else {
                task.run()
            }
        }
    }

    /** print(expr) / log(expr)：单参数，stringify 后写入 logger。 */
    private fun evalPrintLog(name: String, args: List<ExprNode>, ctx: EvalContext): Nothing? {
        if (args.isEmpty()) {
            throw DslRuntimeError("$name() 至少需要 1 个参数")
        }
        // print / log：多参数空格拼接
        val sb = StringBuilder()
        for (i in args.indices) {
            if (i > 0) sb.append(' ')
            val v = ExprEvaluator.eval(args[i], ctx)
            sb.append(DslValueUtils.stringify(v))
        }
        val text = sb.toString()
        val logger = ctx.logger
        if ("print" == name) {
            logger.print(text)
        } else {
            logger.log(text)
        }
        return null
    }

    /**
     * M6-f §8.4：format(fmt, args...) 实现。
     *
     * 仅支持 `%d / %f / %s / %.Nf` 占位符（`%%` 转义除外）；超出则抛
     * [DslRuntimeError]。Number 参数对 %d 取 long、%f/%.Nf 取 double、%s 用 stringify。
     */
    @Suppress("ThrowsCount")
    private fun evalFormat(args: List<ExprNode>, ctx: EvalContext): String {
        if (args.isEmpty()) {
            throw DslRuntimeError("format() 至少需要 1 个参数（格式字符串）")
        }
        val first = ExprEvaluator.eval(args[0], ctx)
        if (first !is String) {
            throw DslRuntimeError("format() 第一参数必须为字符串，实际类型: ${DslValueUtils.classOf(first)}")
        }
        val fmt: String = first
        validateFormatString(fmt)
        val javaArgs = arrayOfNulls<Any?>(args.size - 1)
        for (i in 1 until args.size) {
            javaArgs[i - 1] = ExprEvaluator.eval(args[i], ctx)
        }
        return try {
            formatStringCompat(fmt, coerceFormatArgs(fmt, javaArgs))
        } catch (ex: IllegalArgumentException) {
            throw DslRuntimeError("format() 格式化失败: ${ex.message}")
        }
    }

    /** 仅允许 %d / %f / %s / %.Nf；其余占位符抛错。`%%` 视为转义合法。 */
    private fun validateFormatString(fmt: String) {
        for (m in FORMAT_TOKEN.findAll(fmt)) {
            val tok = m.value
            if ("%%" == tok) continue
            if (!ALLOWED_FORMAT.matches(tok)) {
                throw DslRuntimeError(
                    "format() 不支持的占位符: $tok（仅支持 %d / %f / %s / %.Nf）",
                )
            }
        }
    }

    /** 将 DSL 数值统一转换为 Java 期望的类型（%d→long，%f/%.Nf→double）。 */
    private fun coerceFormatArgs(fmt: String, args: Array<Any?>): Array<Any?> {
        val out = arrayOfNulls<Any?>(args.size)
        var i = 0
        val tok = FORMAT_TOKEN.findAll(fmt).iterator()
        while (tok.hasNext() && i < args.size) {
            val t = tok.next().value
            if ("%%" == t) continue
            val v = args[i]
            if (t.endsWith("d")) {
                if (v is Number) {
                    out[i] = v.toLong()
                } else {
                    throw DslRuntimeError("format() %d 期望 Number，实际: ${DslValueUtils.classOf(v)}")
                }
            } else if (t.endsWith("f")) {
                if (v is Number) {
                    out[i] = v.toDouble()
                } else {
                    throw DslRuntimeError("format() %f 期望 Number，实际: ${DslValueUtils.classOf(v)}")
                }
            } else {
                out[i] = DslValueUtils.stringify(v)
            }
            i++
        }
        return out
    }

    /** 匹配字符串中所有 %... 占位符（含 %%），用于校验。 */
    private val FORMAT_TOKEN = Regex("%(?:%|[^%]*?[a-zA-Z])")

    /** 仅允许的占位符形态：%d / %f / %s / %.Nf（N 为正整数）。 */
    private val ALLOWED_FORMAT = Regex("%(?:d|f|s|\\.\\d+f)")

    /**
     * M6-h Round 2：toast(message, kind="info", duration=3000, position="top")。
     *
     * 支持位置实参与命名实参，任意上下文调用。通过 EvalContext 拿 ToastManager；
     * 未注入时静默忽略（兼容旧测试路径与未集成 ToastManager 的环境）。
     */
    private fun evalToast(call: ExprNode.BuiltinCall, ctx: EvalContext): Nothing? {
        val mgr: ToastManager? = ctx.toastManager
        val ta = resolveToastArgs(call, ctx)
        if (mgr != null) {
            mgr.show(ta.message, ta.kind, ta.duration, ta.position, ta.style)
        }
        return null
    }

    /**
     * toast() 实参对齐：位置参数优先，命名参数次之，剩余项取默认值。
     *
     * `style` 命名参数（仅命名形式，不接受位置参数）按字符串名查
     * [ThemeAccess.current] 当前主题；
     * 未指定 style 时按 `kind` 名查同一表（与既有 4 个内建项 info /
     * success / warning / error 对齐）；都未命中则用 `defaultToastStyle`。
     */
    @Suppress("CyclomaticComplexMethod")
    private fun resolveToastArgs(call: ExprNode.BuiltinCall, ctx: EvalContext): ToastArgs {
        val pos = call.args
        if (pos.isEmpty()) {
            // 若位置缺省，message 必须由命名参数提供
            val m = findNamed(call, "message")
            if (m == null) {
                throw DslRuntimeError("toast() 缺少必填实参 message")
            }
        }
        val messageVal = if (pos.size > 0) ExprEvaluator.eval(pos[0], ctx) else evalNamed(call, "message", ctx)
        val kindVal = if (pos.size >
            1
        ) {
            ExprEvaluator.eval(pos[1], ctx)
        } else {
            evalNamedOrDefault(call, "kind", ctx, "info")
        }
        val durationVal =
            if (pos.size > 2) ExprEvaluator.eval(pos[2], ctx) else evalNamedOrDefault(call, "duration", ctx, 3000.0)
        // position 默认 null：null 表示"调用方未指定 position"，由 ToastLayer 在渲染期
        // 回退到 style.anchor()。这样 toast(..., style = "bottomRight") 的样式自带 anchor
        // 不会被强制 "top" 覆盖。位置参数仍然支持显式传入字符串覆盖样式 anchor。
        val positionVal = if (pos.size > 3) ExprEvaluator.eval(pos[3], ctx) else evalNamed(call, "position", ctx)
        if (pos.size > 4) {
            throw DslRuntimeError("toast() 位置实参过多：期望最多 4，实际 ${pos.size}")
        }
        val message = if (messageVal == null) "" else DslValueUtils.stringify(messageVal)
        val kind = if (kindVal == null) "info" else kindVal.toString()
        val duration = if (durationVal is Number) durationVal.toLong() else 3000L
        // null 透传到 ToastInstance.position；ToastLayer 看到 null 时走 style.anchor()。
        val position = if (positionVal == null) null else positionVal.toString()
        // style 仅支持命名实参（避免与 position 字符串冲突）
        val styleNameVal = evalNamed(call, "style", ctx)
        val style = resolveToastStyle(styleNameVal, kind, ctx)
        return ToastArgs(message, kind, duration, position, style)
    }

    /**
     * 解析 style 命名参数到具体 ToastStyle：
     * 1) style 提供且字符串非空 → 按名字查表，找不到 logger.warn 后退回 kind 路径；
     * 2) 否则按 kind 名查表（4 个内建语义色 / 用户主题里同名 entry）；
     * 3) 都未命中 → 用 defaultToastStyle。
     *
     * 主题来源：[resolveToastTheme]，永不依赖
     * [ThemeAccess] 这个 ThreadLocal——后者只在
     * `UIRenderer.render` 渲染帧期间被 set，事件回调 / await 回包 / 普通 fun 调用
     * 都在帧外，必须从 EvalContext 取当前会话主题。
     */
    private fun resolveToastStyle(styleNameVal: Any?, kind: String, ctx: EvalContext): ToastStyle {
        val theme = resolveToastTheme(ctx)
        if (styleNameVal != null) {
            val styleName = styleNameVal.toString()
            if (styleName.isNotEmpty()) {
                val named = theme.toastStyles[styleName]
                if (named != null) return named
                // 不抛错，仅警告：让 DSL 写错样式名时仍能弹出 toast，便于调试。
                LOGGER.warn("toast(style=\"$styleName\") 在主题中未定义，已回退到 kind=\"$kind\" / 默认样式")
            }
        }
        return theme.resolveToastStyle(kind)
    }

    /**
     * 解析 toast 调用时刻应该使用的 ControlTheme，按以下优先级降级：
     * 1. `ctx.rendererRegistry.controlTheme()`：`local.setTheme`
     *    写入的当前运行时主题，最权威。
     * 2. `ControlTheme.fromLookup(ctx.themeLookup, defaultFallback)`：
     *    编译期合并的 scheme baseline，让纯 DSL 资源里改的颜色 / anchor / 几何
     *    生效（即便客户端没 attach RendererRegistry）。
     * 3. `ThemeAccess.current()`：渲染帧内调用兜底（少数测试 / 渲染期内调用
     *    toast 的 corner case）。
     * 4. `ControlTheme.defaultFallback()`：以上全部为空时的最终兜底。
     *
     * 这里不读 ThemeAccess 优先级有意低于 EvalContext：DSL 中 toast 几乎都是在事件
     * 回调 / await 回包等帧外路径触发，那些路径下 ThemeAccess 总是 fallback，会覆盖掉
     * 用户在主题资源里写的真实样式。
     */
    private fun resolveToastTheme(ctx: EvalContext?): ControlTheme {
        if (ctx != null) {
            val registry = ctx.rendererRegistry
            if (registry != null) {
                return registry.controlTheme()
            }
            val lookup = ctx.themeLookup
            // ThemeLookup.empty() 是单例，identity 比较等价于"无任何编译期 token"。
            // 非空 lookup 走 fromLookup 解码出当前 baseline；fromLookup 内部对缺失 token
            // 自动退化到 fallback 字段，所以即便部分 token 缺失也能正确合成。
            if (lookup !== ThemeLookup.empty()) {
                return ControlTheme.fromLookup(lookup, ControlTheme.defaultFallback())
            }
        }
        // 渲染帧内调用兜底；ThemeAccess 默认 withInitial(defaultFallback) 永不返回 null
        return ThemeAccess.current()
    }

    /** 在 BuiltinCall 命名实参中按名查找；找不到返回 null。 */
    private fun findNamed(call: ExprNode.BuiltinCall, name: String): ExprNode? {
        for (na in call.namedArgs) {
            if (na.name == name) return na.value
        }
        return null
    }

    /** 求 BuiltinCall 命名实参的值；不存在时返回 null。 */
    private fun evalNamed(call: ExprNode.BuiltinCall, name: String, ctx: EvalContext): Any? {
        val v = findNamed(call, name)
        return if (v == null) null else ExprEvaluator.eval(v, ctx)
    }

    /** 求 BuiltinCall 命名实参的值；不存在时返回 defaultValue。 */
    private fun evalNamedOrDefault(
        call: ExprNode.BuiltinCall,
        name: String,
        ctx: EvalContext,
        defaultValue: Any?,
    ): Any? {
        val v = findNamed(call, name)
        return if (v == null) defaultValue else ExprEvaluator.eval(v, ctx)
    }

    /** toast 实参快照。 */
    private class ToastArgs(
        val message: String,
        val kind: String,
        val duration: Long,
        val position: String?,
        val style: ToastStyle,
    )

    // ===== M6-i：animate(name, target?, onComplete?) =====

    /**
     * M6-i：`animate(name: String, target: String? = null, onComplete: (() -> Unit)? = null)`。
     *
     * 语义（任意上下文均可调用，含同步 fun / suspend fun / lambda / 事件回调）：
     * - name 不存在：抛 [DslRuntimeError]（编译期 E761 已尽力前置）
     * - target 非 null 但元素树中未找到该 id：抛 [DslRuntimeError]（E762 留 runtime）
     * - onComplete 必须是可调用值（FunRefValue / BoundFunRefValue / Lambda 等）；
     *   动画播放完成后由 AnimationHandle.onComplete 触发
     */
    private fun evalAnimate(call: ExprNode.BuiltinCall, ctx: EvalContext): Nothing? {
        val args = resolveAnimateArgs(call, ctx)
        val host: AnimationHost? = ctx.animationHost
        if (host == null) {
            // 未注入宿主时静默忽略；与 toast 未注入 ToastManager 时一致
            return null
        }
        val target = resolveAnimateTarget(host, args.target)
        val handle: AnimationHandle? = host.play(target, args.name)
        if (handle == null) {
            throw DslRuntimeError("animate(...) 引用的 animation '${args.name}' 不存在或目标元素未挂接调度器")
        }
        if (args.onComplete != null) {
            val cb = args.onComplete
            handle.onComplete {
                try {
                    CallableInvoker.invokeCallable(cb, emptyList(), ctx, "animate onComplete")
                } catch (ignored: RuntimeException) {
                    // 回调失败已在 invokeCallable 内部消化；本层再加一次防御
                }
            }
        }
        return null
    }

    /** 解析 animate() 的实参（位置 + 命名）；name 不传时抛 DslRuntimeError。 */
    private fun resolveAnimateArgs(call: ExprNode.BuiltinCall, ctx: EvalContext): AnimateArgs {
        val pos = call.args
        val nameVal = if (pos.size > 0) {
            ExprEvaluator.eval(pos[0], ctx)
        } else {
            evalNamed(call, "name", ctx)
        }
        if (nameVal !is String) {
            throw DslRuntimeError("animate() name 实参必须为字符串，实际类型: ${DslValueUtils.classOf(nameVal)}")
        }
        val targetVal = if (pos.size > 1) {
            ExprEvaluator.eval(pos[1], ctx)
        } else {
            evalNamedOrDefault(call, "target", ctx, null)
        }
        val onCompleteVal = if (pos.size > 2) {
            ExprEvaluator.eval(pos[2], ctx)
        } else {
            evalNamedOrDefault(call, "onComplete", ctx, null)
        }
        if (pos.size > 3) {
            throw DslRuntimeError("animate() 位置实参过多：期望最多 3，实际 ${pos.size}")
        }
        val targetId = if (targetVal == null) null else targetVal.toString()
        return AnimateArgs(nameVal, targetId, onCompleteVal)
    }

    /** target=null → root；非空但找不到对应 id → DslRuntimeError（E762 留 runtime 实现）。 */
    private fun resolveAnimateTarget(host: AnimationHost, targetId: String?): UIElement<*>? {
        if (targetId == null) return host.root()
        val hit = host.findElementById(targetId)
        if (hit == null) {
            throw DslRuntimeError("animate(...) 引用的 target id '$targetId' 不存在")
        }
        return hit
    }

    /** animate() 实参快照。 */
    private class AnimateArgs(val name: String, val target: String?, val onComplete: Any?)

    /** 取一个数值参数；个数不符或类型不符直接抛 DslRuntimeError。 */
    private fun num1(name: String, args: List<ExprNode>, ctx: EvalContext): Double {
        if (args.size != 1) {
            throw DslRuntimeError("$name() 仅接受 1 个参数，实际 ${args.size}")
        }
        return CallArgs.toDouble(name, ExprEvaluator.eval(args[0], ctx))
    }

    /** 取两个数值参数。 */
    private fun num2(name: String, args: List<ExprNode>, ctx: EvalContext): DoubleArray {
        if (args.size != 2) {
            throw DslRuntimeError("$name() 仅接受 2 个参数，实际 ${args.size}")
        }
        return doubleArrayOf(
            CallArgs.toDouble(name, ExprEvaluator.eval(args[0], ctx)),
            CallArgs.toDouble(name, ExprEvaluator.eval(args[1], ctx)),
        )
    }

    /** 取 N 个数值参数。 */
    private fun numN(name: String, args: List<ExprNode>, ctx: EvalContext, n: Int): DoubleArray {
        if (args.size != n) {
            throw DslRuntimeError("$name() 仅接受 $n 个参数，实际 ${args.size}")
        }
        val out = DoubleArray(n)
        for (i in 0 until n) {
            out[i] = CallArgs.toDouble(name, ExprEvaluator.eval(args[i], ctx))
        }
        return out
    }

    /** 把 0..255 的 double 通道值四舍五入并 clamp 到 [0,255]。 */
    private fun clampByteChannel(v: Double): Int {
        val rounded = v.roundToLong()
        if (rounded < 0L) return 0
        if (rounded > 255L) return 255
        return rounded.toInt()
    }

    /**
     * HSVA → ARGB int。h:0..360（自动取模归一），s/v/a:0..1（超出自动 clamp）。
     * 输出 0xAARRGGBB。
     */
    @Suppress("CyclomaticComplexMethod")
    private fun hsvaToArgb(h: Double, s: Double, v: Double, a: Double): Int {
        // 归一化色相到 [0, 360)
        var hh = h % 360.0
        if (hh < 0.0) hh += 360.0
        val ss = max(0.0, min(1.0, s))
        val vv = max(0.0, min(1.0, v))
        val aa = max(0.0, min(1.0, a))
        val c = vv * ss
        val x = c * (1.0 - abs((hh / 60.0) % 2.0 - 1.0))
        val m = vv - c
        val r: Double
        val g: Double
        val b: Double
        if (hh < 60.0) {
            r = c
            g = x
            b = 0.0
        } else if (hh < 120.0) {
            r = x
            g = c
            b = 0.0
        } else if (hh < 180.0) {
            r = 0.0
            g = c
            b = x
        } else if (hh < 240.0) {
            r = 0.0
            g = x
            b = c
        } else if (hh < 300.0) {
            r = x
            g = 0.0
            b = c
        } else {
            r = c
            g = 0.0
            b = x
        }
        val ri = mathRoundCompat((r + m) * 255.0).toInt()
        val gi = mathRoundCompat((g + m) * 255.0).toInt()
        val bi = mathRoundCompat((b + m) * 255.0).toInt()
        val ai = mathRoundCompat(aa * 255.0).toInt()
        return (ai shl 24) or (ri shl 16) or (gi shl 8) or bi
    }
}
